<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal-card">
      <header>
        <h3>Edit image metadata</h3>
        <button class="icon-btn" @click="$emit('close')" type="button" aria-label="Close">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="body">
        <p class="hint">
          Name and description are internal labels only — they're not sent to Apple. The image
          itself stays unchanged regardless of state.
        </p>

        <div class="form-row">
          <label>Name <span class="required">*</span></label>
          <input
            v-model="name"
            placeholder="Internal label"
            class="text-input"
            type="text"
          />
        </div>

        <div class="form-row">
          <label>Description <span class="optional">(optional)</span></label>
          <textarea
            v-model="description"
            :maxlength="DESCRIPTION_MAX"
            rows="3"
            placeholder="Where/why this art is used (max 200 chars)"
            class="text-input"
          ></textarea>
          <span class="char-count">{{ description.length }}/{{ DESCRIPTION_MAX }}</span>
        </div>

        <div class="form-row">
          <label>Apple format</label>
          <div class="usage-readonly">
            <span class="usage-readonly-pill" :class="`usage-readonly-${resolvedUsage}`">
              <i :class="resolvedUsage === 'bullet' ? 'fas fa-list-ul' : 'fas fa-image'"></i>
              {{ resolvedUsage === 'bullet' ? 'Bullet icon (1024×1024)' : 'Main image (3840 wide)' }}
            </span>
          </div>
          <p class="usage-hint">
            Locked at upload — Apple's two image formats can't be swapped post-hoc.
            Re-upload as a new image to change the format.
          </p>
        </div>

        <div v-if="errors.length" class="errors">
          <p v-for="(err, i) in errors" :key="i">{{ err }}</p>
        </div>
      </div>

      <footer>
        <button class="btn btn-secondary" type="button" @click="$emit('close')" :disabled="busy">
          Cancel
        </button>
        <button class="btn btn-primary" type="button" @click="save" :disabled="!dirty || busy">
          <span v-if="busy" class="spinner-border spinner-border-sm"></span>
          <span v-else>Save</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
const DESCRIPTION_MAX = 200;

export default {
  name: 'RetentionImageEdit',
  props: {
    image: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      name: this.image.name || '',
      description: this.image.description || '',
      busy: false,
      errors: [],
      DESCRIPTION_MAX
    };
  },
  computed: {
    // Legacy images uploaded before the `usage` field existed are physically
    // FULL_SIZE — surface them as 'main' so the read-only badge isn't blank.
    resolvedUsage() {
      return this.image.usage === 'bullet' ? 'bullet' : 'main';
    },
    dirty() {
      return (
        this.name !== (this.image.name || '') ||
        this.description !== (this.image.description || '')
      );
    }
  },
  methods: {
    async save() {
      this.errors = [];
      if (!this.name.trim()) {
        this.errors.push('Name is required.');
        return;
      }
      if (this.description.length > DESCRIPTION_MAX) {
        this.errors.push(`Description must be ${DESCRIPTION_MAX} characters or fewer.`);
        return;
      }
      this.busy = true;
      try {
        const data = {
          name: this.name.trim(),
          description: this.description.trim() ? this.description.trim() : null
        };
        await this.$store.dispatch('retention/editImage', { id: this.image.id, data });
        this.$emit('updated');
        this.$emit('close');
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Save failed.';
        this.errors.push(msg);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fade 0.15s ease;
}

@keyframes fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.modal-card {
  width: min(480px, calc(100vw - 2rem));
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

header, footer {
  display: flex;
  align-items: center;
  padding: 0.85rem 1.25rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

footer {
  border-bottom: none;
  border-top: 1px solid #dee2e6;
  justify-content: flex-end;
  gap: 0.5rem;
}

header h3 {
  margin: 0;
  font-size: 1.05rem;
  color: #2c3e50;
  flex: 1;
}

.icon-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
}

.icon-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.body {
  padding: 1.25rem;
}

.hint {
  font-size: 0.82rem;
  color: #6c757d;
  margin: 0 0 1rem;
  line-height: 1.4;
}

.form-row {
  margin-bottom: 0.85rem;
}

.form-row label {
  display: block;
  margin-bottom: 0.3rem;
  font-weight: 600;
  font-size: 0.85rem;
  color: #2c3e50;
}

.required {
  color: #e74c3c;
}

.optional {
  color: #95a5a6;
  font-weight: 400;
  font-size: 0.78rem;
}

.text-input {
  width: 100%;
  padding: 0.45rem 0.7rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #2c3e50;
  background: #fff;
  font-family: inherit;
}

.text-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
}

textarea.text-input {
  resize: vertical;
  min-height: 60px;
}

.char-count {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: #95a5a6;
  margin-top: 0.2rem;
}

.usage-readonly {
  display: flex;
  margin-top: 0.2rem;
}

.usage-readonly-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.usage-readonly-main {
  background: rgba(52, 152, 219, 0.12);
  color: #2c5e8a;
  border-color: rgba(52, 152, 219, 0.35);
}

.usage-readonly-bullet {
  background: rgba(155, 89, 182, 0.12);
  color: #6e418c;
  border-color: rgba(155, 89, 182, 0.35);
}

.usage-readonly-pill i {
  font-size: 0.75rem;
}

.usage-hint {
  margin: 0.4rem 0 0;
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.4;
}

.errors {
  margin-top: 0.5rem;
  color: #c0392b;
  font-size: 0.85rem;
}

.errors p {
  margin: 0.2rem 0;
}

.btn {
  padding: 0.45rem 1.1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:disabled {
  background: #a4cef0;
  cursor: not-allowed;
}

.btn-secondary {
  background: #fff;
  color: #34495e;
  border: 1px solid #dee2e6;
}

.btn-secondary:disabled {
  opacity: 0.6;
}
</style>
